import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Input } from '../common';
import { usePricingStore } from '../../stores/pricingStore';
import { validateNumeric } from '../../lib/validation/validators';
import { useCurrency } from '../../lib/utils/currency';
import { useUnitStore } from '../../stores/unitStore';

export function PricingParametersForm() {
  const { t } = useTranslation();
  const { symbol } = useCurrency();
  const { weightUnit } = useUnitStore();
  const massUnit = weightUnit === 'lb' ? 'lb' : 'ton';
  const { parameters, updateParameter, loadDefaults } = usePricingStore();

  // Load defaults on mount
  useEffect(() => {
    loadDefaults();
  }, [loadDefaults]);

  const handleNumberChange = (
    key: keyof typeof parameters,
    value: string
  ) => {
    // Boş string'e izin ver (kullanıcı silme yapıyor olabilir)
    if (value === '') {
      updateParameter(key, '' as any);
      return;
    }

    // Sayı parse edilebiliyorsa kaydet (validasyon blur'da yapılacak)
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      updateParameter(key, numValue as any);
    }
  };

  const getFieldError = (value: number): string | undefined => {
    if (!validateNumeric(value) && value !== 0) {
      return t('pricingParameters.validNumber');
    }
    
    if (value < 0) {
      return t('pricingParameters.noNegative');
    }
    
    return undefined;
  };

  return (
    <Card title={t('pricingParameters.title')}>
      <div className="space-y-6">
        {/* Meat Price - Highlighted */}
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-2 border-blue-200 dark:border-blue-800">
          <Input
            label={t('pricingParameters.meatPrice', { symbol, unit: weightUnit })}
            type="number"
            min="0"
            step="0.01"
            value={parameters.meatPricePerKg ?? ''}
            onChange={(e) => handleNumberChange('meatPricePerKg', e.target.value)}
            error={getFieldError(parameters.meatPricePerKg)}
            required
            helperText={t('pricingParameters.meatPriceHelper')}
            className="font-semibold"
          />
        </div>

        {/* Feed Bonus/Penalty */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={t('pricingParameters.feedBonusRate', { symbol, unit: weightUnit })}
            type="number"
            min="0"
            step="0.01"
            value={parameters.feedBonusRate ?? ''}
            onChange={(e) => handleNumberChange('feedBonusRate', e.target.value)}
            error={getFieldError(parameters.feedBonusRate)}
            required
            helperText={t('pricingParameters.feedBonusHelper')}
          />
          <Input
            label={t('pricingParameters.feedPenaltyRate', { symbol, unit: weightUnit })}
            type="number"
            min="0"
            step="0.01"
            value={parameters.feedPenaltyRate ?? ''}
            onChange={(e) => handleNumberChange('feedPenaltyRate', e.target.value)}
            error={getFieldError(parameters.feedPenaltyRate)}
            required
            helperText={t('pricingParameters.feedPenaltyHelper')}
          />
        </div>

        {/* EPEF Limits and Prices */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            {t('pricingParameters.epefPricing')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Level 1 */}
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                {t('pricingParameters.level')} 1
              </label>
              <Input
                label={t('pricingParameters.epefLimit')}
                type="number"
                min="0"
                step="1"
                value={parameters.epefLimit1 ?? ''}
                onChange={(e) => handleNumberChange('epefLimit1', e.target.value)}
                error={getFieldError(parameters.epefLimit1)}
                required
                placeholder="400"
              />
              <Input
                label={t('pricingParameters.epefPrice', { symbol, unit: weightUnit })}
                type="number"
                min="0"
                step="0.001"
                value={parameters.epefPrice1 ?? ''}
                onChange={(e) => handleNumberChange('epefPrice1', e.target.value)}
                error={getFieldError(parameters.epefPrice1)}
                required
                placeholder="0.10"
                helperText={t('pricingParameters.epefPriceHelper')}
              />
            </div>

            {/* Level 2 */}
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                {t('pricingParameters.level')} 2
              </label>
              <Input
                label={t('pricingParameters.epefLimit')}
                type="number"
                min="0"
                step="1"
                value={parameters.epefLimit2 ?? ''}
                onChange={(e) => handleNumberChange('epefLimit2', e.target.value)}
                error={getFieldError(parameters.epefLimit2)}
                required
                placeholder="450"
              />
              <Input
                label={t('pricingParameters.epefPrice', { symbol, unit: weightUnit })}
                type="number"
                min="0"
                step="0.001"
                value={parameters.epefPrice2 ?? ''}
                onChange={(e) => handleNumberChange('epefPrice2', e.target.value)}
                error={getFieldError(parameters.epefPrice2)}
                required
                placeholder="0.15"
                helperText={t('pricingParameters.epefPriceHelper')}
              />
            </div>

            {/* Level 3 */}
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                {t('pricingParameters.level')} 3
              </label>
              <Input
                label={t('pricingParameters.epefLimit')}
                type="number"
                min="0"
                step="1"
                value={parameters.epefLimit3 ?? ''}
                onChange={(e) => handleNumberChange('epefLimit3', e.target.value)}
                error={getFieldError(parameters.epefLimit3)}
                required
                placeholder="500"
              />
              <Input
                label={t('pricingParameters.epefPrice', { symbol, unit: weightUnit })}
                type="number"
                min="0"
                step="0.001"
                value={parameters.epefPrice3 ?? ''}
                onChange={(e) => handleNumberChange('epefPrice3', e.target.value)}
                error={getFieldError(parameters.epefPrice3)}
                required
                placeholder="0.20"
                helperText={t('pricingParameters.epefPriceHelper')}
              />
            </div>
          </div>
        </div>

        {/* Chick Mortality Limit */}
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border-2 border-amber-200 dark:border-amber-800">
          <Input
            label={t('pricingParameters.chickMortalityLimit')}
            type="number"
            min="0"
            step="0.1"
            value={parameters.chickMortalityLimit ?? ''}
            onChange={(e) => handleNumberChange('chickMortalityLimit', e.target.value)}
            error={getFieldError(parameters.chickMortalityLimit)}
            required
            helperText={t('pricingParameters.chickMortalityHelper')}
            className="font-semibold"
          />
        </div>

        {/* Chick Bonus/Penalty */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={t('pricingParameters.chickBonusRate', { symbol })}
            type="number"
            min="0"
            step="0.01"
            value={parameters.chickBonusRate ?? ''}
            onChange={(e) => handleNumberChange('chickBonusRate', e.target.value)}
            error={getFieldError(parameters.chickBonusRate)}
            required
            helperText={t('pricingParameters.chickBonusHelper')}
          />
          <Input
            label={t('pricingParameters.chickPenaltyRate', { symbol })}
            type="number"
            min="0"
            step="0.01"
            value={parameters.chickPenaltyRate ?? ''}
            onChange={(e) => handleNumberChange('chickPenaltyRate', e.target.value)}
            error={getFieldError(parameters.chickPenaltyRate)}
            required
            helperText={t('pricingParameters.chickPenaltyHelper')}
          />
        </div>

        {/* Coal Support */}
        <Input
          label={t('pricingParameters.coalSupport', { symbol, massUnit })}
          type="number"
          min="0"
          step="0.001"
          value={parameters.coalSupportPerTon ?? ''}
          onChange={(e) => handleNumberChange('coalSupportPerTon', e.target.value)}
          error={getFieldError(parameters.coalSupportPerTon)}
          required
          helperText={t('pricingParameters.coalSupportHelper')}
        />
      </div>

      {/* Info Box */}
      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {t('pricingParameters.defaultsInfo')}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}
